import {
  buildEditableFieldsConfig,
  DeliveryDetailsWidget,
  getIncotermsInfo,
} from "@fero/components";
import { useGetOrganisationDeliveryLocations } from "@fero/hooks";
import type { StorageLocationView } from "adl-gen/ferovinum/app/api";
import type { FormikForm } from "components/types/formik-types";
import { useMemo } from "react";
import type { DeliveryFormValues } from "../organisation-repurchase-delivery-page-view";
import {
  IN_BOND_DELIVERY_INCOTERMS,
  OUT_OF_BOND_DELIVERY_INCOTERMS,
} from "./delivery-form";

export const SavedDeliveryOption = ({
  form,
  storageLocation,
}: {
  form: FormikForm<DeliveryFormValues>;
  storageLocation: StorageLocationView;
}) => {
  const orgSavedLocationEditableFieldConfig = useMemo(() => {
    return buildEditableFieldsConfig(false, {
      savedAddressOptions: true,
      deliveryIncoterms: true,
      deliveryInstructions: true,
      deliveryDateAndTime: true,
      deliveryContact: true,
      deliveryContactNumber:
        storageLocation.deliveryInstructionConfig?.telephoneRequired ?? true,
      deliveryContactEmail:
        storageLocation.deliveryInstructionConfig?.emailRequired ?? true,
    });
  }, [
    storageLocation.deliveryInstructionConfig?.emailRequired,
    storageLocation.deliveryInstructionConfig?.telephoneRequired,
  ]);

  const { data, isLoading, isError } = useGetOrganisationDeliveryLocations();
  const organisationDeliveryLocations = useMemo(
    () =>
      data
        ? data.map((loc) => ({
            deliveryPointName: loc.value.deliveryPointName,
            address: {
              streetName: loc.value.streetName,
              line1: loc.value.addressLine1 ?? "",
              line2: loc.value.addressLine2 ?? "",
              town: loc.value.town,
              postCode: loc.value.postCode,
              country: loc.value.country,
              // TODO state is not supported
              state: null,
            },
            contact: {
              name: loc.value.deliveryContact ?? "",
              email: loc.value.deliveryContactEmail,
              phoneNumber: loc.value.deliveryContactNumber,
            },
            instruction: loc.value.deliveryInstructions,
            bonded: loc.value.bonded,
          }))
        : [],
    [data],
  );

  if (isLoading) {
    return <div />;
  }

  if (isError) {
    return <div />;
  }

  return (
    <DeliveryDetailsWidget
      form={form}
      formKey="savedAddress"
      savedDeliveryInfoList={organisationDeliveryLocations}
      storageLocationName={storageLocation.storageLocationName}
      deliveryCountries={storageLocation.deliveryCountries}
      deliveryInstructionConfig={storageLocation.deliveryInstructionConfig}
      incoterms={{
        values: form.values.savedAddress?.bonded
          ? IN_BOND_INCOTERMS
          : OUT_OF_BOND_INCOTERMS,
        clearOnBondedChange: true,
      }}
      editableFields={orgSavedLocationEditableFieldConfig}
    />
  );
};

const IN_BOND_INCOTERMS = getIncotermsInfo(IN_BOND_DELIVERY_INCOTERMS);
const OUT_OF_BOND_INCOTERMS = getIncotermsInfo(OUT_OF_BOND_DELIVERY_INCOTERMS);
